<template>
  <div class="theme-management">
    <div class="page-header">
      <h1>主题管理</h1>
      <div class="header-actions">
        <el-button type="primary" @click="showCreateDialog = true">
          <el-icon><Plus /></el-icon>
          新增主题
        </el-button>
        <el-button @click="loadThemes">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 搜索和筛选 -->
    <div class="search-bar">
      <el-input
        v-model="searchQuery"
        placeholder="搜索主题名称"
        style="width: 300px; margin-right: 10px;"
        clearable
        @keyup.enter="loadThemes"
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
      <el-select
        v-model="filterPublished"
        placeholder="上下架状态"
        style="width: 150px; margin-right: 10px;"
        clearable
        @change="loadThemes"
      >
        <el-option label="已上架" value="1" />
        <el-option label="已下架" value="0" />
      </el-select>
      <el-button type="primary" @click="loadThemes">搜索</el-button>
    </div>

    <!-- 主题列表 -->
    <div class="theme-list">
      <el-table
        v-loading="loading"
        :data="themes"
        stripe
        style="width: 100%"
        empty-text="暂无主题"
      >
        <el-table-column prop="name" label="主题名称" min-width="200" />
        <el-table-column label="图片" width="120">
          <template #default="{ row }">
            <el-image
              v-if="row.image_url"
              :src="row.image_url"
              :preview-src-list="[]"
              :preview-disabled="true"
              style="width: 80px; height: 80px; border-radius: 4px; cursor: pointer;"
              fit="cover"
              :lazy="true"
              @click="openThemeImagePreview(row.image_url)"
            />
            <span v-else class="image-placeholder">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="weight" label="权重" width="100" sortable>
          <template #default="{ row }">
            <el-tag>{{ row.weight }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="上下架状态" width="120">
          <template #default="{ row }">
            <el-tag :type="row.is_published ? 'success' : 'info'" size="small">
              {{ row.is_published ? '已上架' : '已下架' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="updated_at" label="更新时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.updated_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="300" fixed="right">
          <template #default="{ row }">
            <el-button
              :type="row.is_published ? 'warning' : 'success'"
              size="small"
              @click="togglePublish(row)"
            >
              {{ row.is_published ? '下架' : '上架' }}
            </el-button>
            <el-button
              type="primary"
              size="small"
              @click="editTheme(row)"
            >
              编辑
            </el-button>
            <el-button
              type="danger"
              size="small"
              @click="deleteTheme(row.id)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="loadThemes"
          @current-change="loadThemes"
        />
      </div>
    </div>

    <!-- 创建/编辑对话框 -->
    <el-dialog
      v-model="showCreateDialog"
      :title="editingTheme ? '编辑主题' : '新增主题'"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form
        ref="themeFormRef"
        :model="themeForm"
        :rules="themeRules"
        label-width="100px"
      >
        <el-form-item label="主题名称" prop="name">
          <el-input v-model="themeForm.name" placeholder="请输入主题名称" />
        </el-form-item>
        <el-form-item label="主题图片" prop="image_url">
          <el-upload
            class="theme-image-uploader"
            :show-file-list="false"
            :before-upload="beforeImageUpload"
            :http-request="handleImageUpload"
            accept="image/*"
          >
            <el-image
              v-if="themeForm.image_url"
              :src="themeForm.image_url"
              class="theme-image"
              fit="cover"
              :preview-src-list="[]"
              :preview-disabled="true"
            />
            <el-icon v-else class="uploader-icon"><Plus /></el-icon>
          </el-upload>
          <div style="font-size: 12px; color: #909399; margin-top: 8px;">
            支持 JPG、PNG、GIF 等格式，大小不超过 5MB
          </div>
        </el-form-item>
        <el-form-item label="权重" prop="weight">
          <el-input-number
            v-model="themeForm.weight"
            :min="0"
            :max="9999"
            placeholder="权重越大排序越靠前"
            style="width: 100%;"
          />
          <div style="font-size: 12px; color: #909399; margin-top: 4px;">
            数值越大，在微信接口中排序越靠前，默认为0
          </div>
        </el-form-item>
        <!-- 颜色配置 -->
        <el-divider>颜色配置</el-divider>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="渐变色1">
              <el-input v-model="themeForm.color_config.grad_1" placeholder="例如: FFFFFF" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="渐变色2">
              <el-input v-model="themeForm.color_config.grad_2" placeholder="例如: A03456" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="边框色">
              <el-input v-model="themeForm.color_config.border" placeholder="例如: 4A5C6D" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="侧栏未选中">
              <el-input v-model="themeForm.color_config.side_bar_inactive" placeholder="例如: 7D345F" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="文字颜色">
              <el-input v-model="themeForm.color_config.text" placeholder="例如: 456ABC" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="预留色">
              <el-input v-model="themeForm.color_config.reserve" placeholder="例如: 1A2B3C" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 底图配置（上传到七牛云） -->
        <el-divider>底图配置</el-divider>
        <el-form-item label="底图 1">
          <el-upload
            class="theme-image-uploader"
            :show-file-list="false"
            :before-upload="beforeImageUpload"
            :http-request="(options) => handleBgImageUpload('bg_1', options)"
            accept="image/*"
          >
            <el-image
              v-if="themeForm.bg_img_config.bg_1"
              :src="themeForm.bg_img_config.bg_1"
              class="theme-image"
              fit="cover"
              :preview-src-list="[]"
              :preview-disabled="true"
            />
            <el-icon v-else class="uploader-icon"><Plus /></el-icon>
          </el-upload>
          <div style="font-size: 12px; color: #909399; margin-top: 4px;">
            当前地址：{{ themeForm.bg_img_config.bg_1 || '-' }}
          </div>
        </el-form-item>
        <el-form-item label="底图 2">
          <el-upload
            class="theme-image-uploader"
            :show-file-list="false"
            :before-upload="beforeImageUpload"
            :http-request="(options) => handleBgImageUpload('bg_2', options)"
            accept="image/*"
          >
            <el-image
              v-if="themeForm.bg_img_config.bg_2"
              :src="themeForm.bg_img_config.bg_2"
              class="theme-image"
              fit="cover"
              :preview-src-list="[]"
              :preview-disabled="true"
            />
            <el-icon v-else class="uploader-icon"><Plus /></el-icon>
          </el-upload>
          <div style="font-size: 12px; color: #909399; margin-top: 4px;">
            当前地址：{{ themeForm.bg_img_config.bg_2 || '-' }}
          </div>
        </el-form-item>
        <el-form-item label="底图 3">
          <el-upload
            class="theme-image-uploader"
            :show-file-list="false"
            :before-upload="beforeImageUpload"
            :http-request="(options) => handleBgImageUpload('bg_3', options)"
            accept="image/*"
          >
            <el-image
              v-if="themeForm.bg_img_config.bg_3"
              :src="themeForm.bg_img_config.bg_3"
              class="theme-image"
              fit="cover"
              :preview-src-list="[]"
              :preview-disabled="true"
            />
            <el-icon v-else class="uploader-icon"><Plus /></el-icon>
          </el-upload>
          <div style="font-size: 12px; color: #909399; margin-top: 4px;">
            当前地址：{{ themeForm.bg_img_config.bg_3 || '-' }}
          </div>
        </el-form-item>
        <el-form-item label="上下架状态" prop="is_published">
          <el-radio-group v-model="themeForm.is_published">
            <el-radio :label="1">上架</el-radio>
            <el-radio :label="0">下架</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="themeForm.image_url" label="图片预览">
          <el-image
            :src="themeForm.image_url"
            style="width: 200px; height: 200px; border-radius: 4px; cursor: pointer;"
            fit="cover"
            :preview-src-list="[]"
            :preview-disabled="true"
            @click="openThemeImagePreview(themeForm.image_url)"
          />
        </el-form-item>
      </el-form>
      
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showCreateDialog = false">取消</el-button>
          <el-button
            type="primary"
            :loading="saving"
            @click="saveTheme"
          >
            保存
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 主题图片预览对话框 -->
    <el-dialog
      v-model="imagePreviewVisible"
      title="主题图片预览"
      width="600px"
      align-center
    >
      <div
        v-if="imagePreviewUrl"
        style="text-align: center; padding: 10px 0;"
      >
        <el-image
          :src="imagePreviewUrl"
          fit="contain"
          :preview-src-list="[]"
          :preview-disabled="true"
          style="max-width: 100%; max-height: 400px; margin: 0 auto; display: inline-block;"
        />
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="imagePreviewVisible = false">关闭</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Refresh, Search } from '@element-plus/icons-vue';
import {
  getThemes,
  createTheme,
  updateTheme,
  deleteTheme as deleteThemeAPI,
  togglePublishTheme,
  uploadThemeImage
} from '@/api/theme';

export default {
  name: 'ThemeManagement',
  components: {
    Plus,
    Refresh,
    Search
  },
  setup() {
    const loading = ref(false);
    const saving = ref(false);
    const themes = ref([]);
    const total = ref(0);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const searchQuery = ref('');
    const filterPublished = ref('');
    const showCreateDialog = ref(false);
    const editingTheme = ref(null);
    const themeFormRef = ref(null);
    const imagePreviewVisible = ref(false);
    const imagePreviewUrl = ref('');

    const themeForm = reactive({
      name: '',
      image_url: '',
      weight: 0,
      is_published: 0,
      color_config: {
        grad_1: 'FFFFFF',
        grad_2: 'A03456',
        border: '4A5C6D',
        side_bar_inactive: '7D345F',
        text: '456ABC',
        reserve: '1A2B3C'
      },
      bg_img_config: {
        bg_1: '',
        bg_2: '',
        bg_3: ''
      }
    });

    const themeRules = {
      name: [
        { required: true, message: '请输入主题名称', trigger: 'blur' }
      ],
      image_url: [
        { required: true, message: '请输入图片地址', trigger: 'blur' },
        { type: 'url', message: '请输入有效的URL地址', trigger: 'blur' }
      ],
      weight: [
        { required: true, message: '请输入权重', trigger: 'blur' }
      ]
    };

    const openThemeImagePreview = (url) => {
      if (!url) return;
      imagePreviewUrl.value = url;
      imagePreviewVisible.value = true;
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    };

    const loadThemes = async () => {
      loading.value = true;
      try {
        const params = {
          page: currentPage.value,
          page_size: pageSize.value
        };
        if (searchQuery.value) {
          params.query = searchQuery.value;
        }
        if (filterPublished.value !== '') {
          params.is_published = filterPublished.value;
        }

        const response = await getThemes(params);
        if (response.success) {
          themes.value = response.data.list;
          total.value = response.data.total;
        } else {
          ElMessage.error(response.message || '获取主题列表失败');
        }
      } catch (error) {
        console.error('获取主题列表失败:', error);
        ElMessage.error('获取主题列表失败');
      } finally {
        loading.value = false;
      }
    };

    const resetForm = () => {
      themeForm.name = '';
      themeForm.image_url = '';
      themeForm.weight = 0;
      themeForm.is_published = 0;
      themeForm.color_config = {
        grad_1: 'FFFFFF',
        grad_2: 'A03456',
        border: '4A5C6D',
        side_bar_inactive: '7D345F',
        text: '456ABC',
        reserve: '1A2B3C'
      };
      themeForm.bg_img_config = {
        bg_1: '',
        bg_2: '',
        bg_3: ''
      };
      editingTheme.value = null;
      if (themeFormRef.value) {
        themeFormRef.value.clearValidate();
      }
    };

    const editTheme = (theme) => {
      editingTheme.value = theme;
      themeForm.name = theme.name;
      themeForm.image_url = theme.image_url;
      themeForm.weight = theme.weight;
      themeForm.is_published = theme.is_published;
      // 颜色配置
      themeForm.color_config = {
        grad_1: theme.color_config?.grad_1 || 'FFFFFF',
        grad_2: theme.color_config?.grad_2 || 'A03456',
        border: theme.color_config?.border || '4A5C6D',
        side_bar_inactive: theme.color_config?.side_bar_inactive || '7D345F',
        text: theme.color_config?.text || '456ABC',
        reserve: theme.color_config?.reserve || '1A2B3C'
      };
      // 底图配置
      themeForm.bg_img_config = {
        bg_1: theme.bg_img_config?.bg_1 || '',
        bg_2: theme.bg_img_config?.bg_2 || '',
        bg_3: theme.bg_img_config?.bg_3 || ''
      };
      showCreateDialog.value = true;
    };

    const saveTheme = async () => {
      if (!themeFormRef.value) return;
      
      await themeFormRef.value.validate(async (valid) => {
        if (!valid) return;

        saving.value = true;
        try {
          if (editingTheme.value) {
            // 更新
            const response = await updateTheme(editingTheme.value.id, themeForm);
            if (response.success) {
              ElMessage.success('更新成功');
              showCreateDialog.value = false;
              resetForm();
              loadThemes();
            } else {
              ElMessage.error(response.message || '更新失败');
            }
          } else {
            // 创建
            const response = await createTheme(themeForm);
            if (response.success) {
              ElMessage.success('创建成功');
              showCreateDialog.value = false;
              resetForm();
              loadThemes();
            } else {
              ElMessage.error(response.message || '创建失败');
            }
          }
        } catch (error) {
          console.error('保存主题失败:', error);
          ElMessage.error('保存主题失败');
        } finally {
          saving.value = false;
        }
      });
    };

    const togglePublish = async (theme) => {
      try {
        const response = await togglePublishTheme(theme.id);
        if (response.success) {
          ElMessage.success('操作成功');
          loadThemes();
        } else {
          ElMessage.error(response.message || '操作失败');
        }
      } catch (error) {
        console.error('切换上下架状态失败:', error);
        ElMessage.error('操作失败');
      }
    };

    const deleteTheme = async (themeId) => {
      try {
        await ElMessageBox.confirm('确定要删除这个主题吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });

        const response = await deleteThemeAPI(themeId);
        if (response.success) {
          ElMessage.success('删除成功');
          loadThemes();
        } else {
          ElMessage.error(response.message || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除主题失败:', error);
          ElMessage.error('删除失败');
        }
      }
    };

    // 图片上传前检查
    const beforeImageUpload = (file) => {
      const isValidType = file.type.startsWith('image/');
      const isLt5M = file.size / 1024 / 1024 < 5;

      if (!isValidType) {
        ElMessage.error('只能上传图片文件!');
        return false;
      }
      if (!isLt5M) {
        ElMessage.error('图片大小不能超过 5MB!');
        return false;
      }
      return true;
    };

    // 处理主图上传
    const handleImageUpload = async (options) => {
      const { file } = options;
      try {
        const response = await uploadThemeImage(file);
        if (response && response.success && response.data) {
          // 保存图片URL（七牛云CDN地址）
          themeForm.image_url = response.data.url;
          ElMessage.success('图片上传成功');
        } else {
          const errorMsg = (response && response.message) || '图片上传失败';
          ElMessage.error(errorMsg);
          console.error('图片上传失败:', response);
        }
      } catch (error) {
        console.error('图片上传异常:', error);
        ElMessage.error('图片上传失败: ' + (error.message || error));
      }
    };

    // 处理底图上传（bg_1/bg_2/bg_3）
    const handleBgImageUpload = async (key, options) => {
      const { file } = options;
      try {
        const response = await uploadThemeImage(file);
        if (response && response.success && response.data) {
          themeForm.bg_img_config[key] = response.data.url;
          ElMessage.success('底图上传成功');
        } else {
          const errorMsg = (response && response.message) || '底图上传失败';
          ElMessage.error(errorMsg);
          console.error('底图上传失败:', response);
        }
      } catch (error) {
        console.error('底图上传异常:', error);
        ElMessage.error('底图上传失败: ' + (error.message || error));
      }
    };

    onMounted(() => {
      loadThemes();
    });

    return {
      loading,
      saving,
      themes,
      total,
      currentPage,
      pageSize,
      searchQuery,
      filterPublished,
      showCreateDialog,
      editingTheme,
      themeFormRef,
      themeForm,
      themeRules,
      imagePreviewVisible,
      imagePreviewUrl,
      formatDate,
      loadThemes,
      editTheme,
      saveTheme,
      togglePublish,
      deleteTheme,
      resetForm,
      beforeImageUpload,
      handleImageUpload,
      handleBgImageUpload,
      openThemeImagePreview
    };
  }
};
</script>

<style scoped>
.theme-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.search-bar {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.theme-list {
  background: #fff;
  border-radius: 4px;
  padding: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.theme-image-uploader {
  display: inline-block;
}

.theme-image-uploader :deep(.el-upload) {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.theme-image-uploader :deep(.el-upload:hover) {
  border-color: #409eff;
}

.uploader-icon {
  font-size: 28px;
  color: #8c939d;
}

.theme-image {
  width: 200px;
  height: 200px;
  display: block;
}

.image-placeholder {
  color: #999;
  font-size: 12px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>

